import { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, Trash2, Edit3, ArrowUpDown } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { formatCurrency, formatDate, formatDuration } from '../lib/utils';
import type { Order } from '../types';

type SortKey = 'createdAt' | 'customerName' | 'total' | 'status';
type SortDir = 'asc' | 'desc';

interface OrdersTableProps {
  orders: Order[];
  isAdmin: boolean;
  onView: (order: Order) => void;
  onEdit: (order: Order) => void;
  onDelete: (orderId: string) => void;
}

export function OrdersTable({ orders, isAdmin, onView, onEdit, onDelete }: OrdersTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sorted = [...orders].sort((a, b) => {
    let aVal: any = a[sortKey];
    let bVal: any = b[sortKey];
    if (sortKey === 'createdAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3.5 h-3.5 text-oem-400" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-accent-600" /> : <ChevronDown className="w-3.5 h-3.5 text-accent-600" />;
  };

  return (
    <div className="oem-card overflow-hidden">
      <div className="px-6 py-4 border-b border-oem-200 flex items-center justify-between bg-white">
        <h3 className="font-display text-lg text-oem-navy">
          Orders <span className="text-oem-muted font-sans text-sm">({orders.length})</span>
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-oem-50">
              <th className="table-header cursor-pointer hover:text-oem-700" onClick={() => handleSort('createdAt')}>
                <span className="flex items-center gap-1">Date <SortIcon column="createdAt" /></span>
              </th>
              <th className="table-header cursor-pointer hover:text-oem-700" onClick={() => handleSort('customerName')}>
                <span className="flex items-center gap-1">Customer <SortIcon column="customerName" /></span>
              </th>
              <th className="table-header">Parts</th>
              <th className="table-header cursor-pointer hover:text-oem-700" onClick={() => handleSort('total')}>
                <span className="flex items-center gap-1">Total <SortIcon column="total" /></span>
              </th>
              <th className="table-header">Time to Close</th>
              <th className="table-header cursor-pointer hover:text-oem-700" onClick={() => handleSort('status')}>
                <span className="flex items-center gap-1">Status <SortIcon column="status" /></span>
              </th>
              <th className="table-header">Source</th>
              <th className="table-header text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-oem-100">
            {paginated.map((order) => {
              const timeToClose = order.checkoutSuccess && order.sessionStart
                ? new Date(order.checkoutSuccess).getTime() - new Date(order.sessionStart).getTime()
                : null;

              return (
                <tr
                  key={order.id}
                  className={`hover:bg-oem-50 transition-colors ${order.abandoned ? 'bg-violet-50' : 'bg-white'}`}
                >
                  <td className="table-cell whitespace-nowrap">
                    <div>
                      <p>{formatDate(order.createdAt)}</p>
                      {order.poNumber && (
                        <p className="text-xs text-oem-muted font-mono mt-0.5">PO #{order.poNumber}</p>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div>
                      <p className="font-medium text-oem-900">{order.customerName}</p>
                      <p className="text-xs text-oem-400">{order.customerEmail}</p>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-wrap gap-1">
                      {order.partNumbers.slice(0, 3).map((part, i) => (
                        <span key={i} className="px-2 py-0.5 bg-oem-100 rounded text-xs text-oem-600 font-mono">
                          {part}
                        </span>
                      ))}
                      {order.partNumbers.length > 3 && (
                        <span className="px-2 py-0.5 text-xs text-oem-400">+{order.partNumbers.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="table-cell font-medium text-oem-900">{formatCurrency(order.total)}</td>
                  <td className="table-cell">
                    {timeToClose ? (
                      <span className="text-sm text-oem-700">{formatDuration(timeToClose)}</span>
                    ) : (
                      <span className="text-oem-400 text-sm">—</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={order.status} size="sm" />
                  </td>
                  <td className="table-cell">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      order.source === 'live'
                        ? 'bg-oem-100 text-oem-800'
                        : 'bg-accent-100 text-accent-700 border border-accent-200'
                    }`}>
                      {order.source === 'live' ? 'Live' : 'Override'}
                    </span>
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onView(order)}
                        className="p-1.5 rounded-lg hover:bg-oem-100 text-oem-500 transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => onEdit(order)}
                            className="p-1.5 rounded-lg hover:bg-oem-100 text-oem-500 transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onDelete(order.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-oem-500 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-oem-200 flex items-center justify-between bg-white">
          <p className="text-sm text-oem-500">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, sorted.length)} of {sorted.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-oem-200 text-sm text-oem-600 disabled:opacity-40 hover:bg-oem-50"
            >
              Previous
            </button>
            <span className="text-sm text-oem-500">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border border-oem-200 text-sm text-oem-600 disabled:opacity-40 hover:bg-oem-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
